import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Upload, 
  message, 
  Tag, 
  Typography, 
  Badge,
  Descriptions,
  Steps,
  Tabs,
  Spin
} from 'antd';
import { 
  DollarOutlined, 
  CreditCardOutlined, 
  BankOutlined, 
  MoneyCollectOutlined,
  UploadOutlined,
  PrinterOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { getUserData } from '../../../hooks/Auth';

const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const API_BASE_URL = "https://techcrew.pk/ulc/";

const RenterPayment = () => {
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const user = getUserData();
  const userEmail = user?.user?.email;

  const paymentMethods = [
    { value: 'Credit Card', icon: <CreditCardOutlined />, label: 'Credit Card' },
    { value: 'Bank Transfer', icon: <BankOutlined />, label: 'Bank Transfer' },
    { value: 'Cash', icon: <MoneyCollectOutlined />, label: 'Cash' },
    { value: 'Other', icon: <DollarOutlined />, label: 'Other' }
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, paymentsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}Get_RenterOrders.php`, { params: { email: userEmail } }),
        axios.get(`${API_BASE_URL}Get_RenterPayments.php`, { params: { email: userEmail } })
      ]);

      if (ordersRes.data.status.code === 200 && paymentsRes.data.status.code === 200) {
        setOrders(ordersRes.data.data);
        setPayments(paymentsRes.data.data);
      } else {
        throw new Error(ordersRes.data.status.message || paymentsRes.data.status.message || "Failed to fetch data");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      message.error(error.message || "Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };
// Individual field states
const [orderId, setOrderId] = useState('');
const [amount, setAmount] = useState(0);
const [paymentMethod, setPaymentMethod] = useState('');
const [transactionId, setTransactionId] = useState('');
const [notes, setNotes] = useState('');
// Field validation states
const [isAmountValid, setIsAmountValid] = useState(false);
const [isPaymentMethodValid, setIsPaymentMethodValid] = useState(false);
const [isTransactionIdValid, setIsTransactionIdValid] = useState(false);

// Field touch states
const [isAmountTouched, setIsAmountTouched] = useState(false);
const [isPaymentMethodTouched, setIsPaymentMethodTouched] = useState(false);
const [isTransactionIdTouched, setIsTransactionIdTouched] = useState(false);

// Handler functions
const handleAmountChange = (e) => {
  const value = parseFloat(e.target.value) || 0;
  setAmount(value);
  setIsAmountValid(value > 0);
  setIsAmountTouched(true);
  form.setFieldsValue({ amount: value });
};

const handlePaymentMethodChange = (value) => {
  setPaymentMethod(value);
  setIsPaymentMethodValid(!!value);
  setIsPaymentMethodTouched(true);
  form.setFieldsValue({ payment_method: value });
};

const handleTransactionIdChange = (e) => {
  const value = e.target.value;
  setTransactionId(value);
  setIsTransactionIdValid(value.length > 0);
  setIsTransactionIdTouched(true);
  form.setFieldsValue({ transaction_id: value });
};

const handleNotesChange = (e) => {
  setNotes(e.target.value);
  form.setFieldsValue({ notes: e.target.value });
};


  const handlePayment = async () => {
    try {
      setModalLoading(true);
      const values = await form.validateFields();
      
      const formData = new FormData();
      formData.append('order_id', currentOrder.order_id);
      formData.append('email', userEmail);
      formData.append('amount', amount);
      formData.append('payment_method', paymentMethod);
      formData.append('transaction_id', transactionId);
      formData.append('notes', notes || '');
      
      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append('receipt', fileList[0].originFileObj);
      }

      const { data } = await axios.post(`${API_BASE_URL}Process_Payment.php`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (data.status.code === 200) {
        message.success(data.status.message);
        setVisible(false);
        form.resetFields();
        setAmount(0);
    setPaymentMethod('');
    setTransactionId('');
    setNotes('');
    setIsAmountValid(false);
    setIsPaymentMethodValid(false);
    setIsTransactionIdValid(false);
    setIsAmountTouched(false);
    setIsPaymentMethodTouched(false);
    setIsTransactionIdTouched(false);
        setFileList([]);
        fetchData();
        setCurrentStep(0);
      } else {
        throw new Error(data.status.message || "Payment failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      message.error(error.response?.data?.status?.message || error.message || "Payment failed");
    } finally {
      setModalLoading(false);
    }
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'application/pdf';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG/PDF files!');
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('File must smaller than 5MB!');
      return Upload.LIST_IGNORE;
    }
    return false; // Return false to handle upload manually
  };

  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const showPaymentModal = (order) => {
    setCurrentOrder(order);
    setVisible(true);
    form.setFieldsValue({
      amount: order.grand_total,
      payment_method: undefined
    });
  };

  const printReceipt = (payment) => {
    if (payment.receipt_path) {
      window.open(`${API_BASE_URL}uploads/payment/${payment.receipt_path}`, '_blank');
    } else {
      message.warning('No receipt available for this payment');
    }
  };

  const orderColumns = [
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      render: (id) => <Text strong>#{id}</Text>
    },
    {
      title: 'Date',
      dataIndex: 'order_date',
      key: 'date',
      render: (date) => moment(date).format('DD MMM YYYY')
    },
    {
      title: 'Amount',
      dataIndex: 'grand_total',
      key: 'amount',
      render: (amount) => (
        <Text strong>
          {parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: "SAR",
          })}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={
            status === "Completed"
              ? "green"
              : status === "Confirmed"
              ? "blue"
              : status === "Pending"
              ? "orange"
              : "red"
          }
        >
          {status}
        </Tag>
      )
    },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      key: 'payment',
      render: (status) => (
        <Badge
          status={
            status === "Paid"
              ? "success"
              : status === "Partially Paid"
              ? "processing"
              : "error"
          }
          text={status}
        />
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="primary" 
          icon={<DollarOutlined />}
          onClick={() => showPaymentModal(record)}
          disabled={record.payment_status === 'Paid'}
        >
          Pay Now
        </Button>
      )
    }
  ];

  const paymentColumns = [
    {
      title: 'Payment ID',
      dataIndex: 'payment_id',
      key: 'payment_id',
      render: (id) => <Text strong>#{id}</Text>
    },
    {
      title: 'Order ID',
      dataIndex: 'order_id',
      key: 'order_id',
      render: (id) => <Text>#{id}</Text>
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong>
          {parseFloat(amount).toLocaleString("en-US", {
            style: "currency",
            currency: "SAR",
          })}
        </Text>
      )
    },
    {
      title: 'Method',
      dataIndex: 'payment_method',
      key: 'payment_method',
      render: (method) => {
        const methodInfo = paymentMethods.find(m => m.value === method);
        return (
          <Tag icon={methodInfo?.icon} color="blue">
            {method}
          </Tag>
        );
      }
    },
    {
      title: 'Date',
      dataIndex: 'payment_date',
      key: 'payment_date',
      render: (date) => moment(date).format('DD MMM YYYY HH:mm')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={
            status === "Completed"
              ? "green"
              : status === "Pending"
              ? "orange"
              : status === "Failed"
              ? "red"
              : "purple"
          }
        >
          {status}
        </Tag>
      )
    },
    {
      title: 'Receipt',
      key: 'receipt',
      render: (_, record) => (
        record.receipt_path ? (
          <Button 
            icon={<PrinterOutlined />} 
            onClick={() => printReceipt(record)}
          >
            View Receipt
          </Button>
        ) : (
          <Text type="secondary">No receipt</Text>
        )
      )
    }
  ];

  useEffect(() => {
    if (userEmail) {
      fetchData();
    }
  }, [userEmail]);

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DollarOutlined style={{ fontSize: '24px', color: '#52c41a', marginRight: '16px' }} />
            <Title level={4} style={{ margin: 0 }}>Payment Management</Title>
          </div>
        }
        bordered={false}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="My Orders" key="1">
            <Table
              columns={orderColumns}
              dataSource={orders}
              rowKey="order_id"
              loading={loading}
              scroll={{ x: true }}
              bordered
            />
          </TabPane>
          <TabPane tab="Payment History" key="2">
            <Table
              columns={paymentColumns}
              dataSource={payments}
              rowKey="payment_id"
              loading={loading}
              scroll={{ x: true }}
              bordered
            />
          </TabPane>
        </Tabs>
      </Card>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <DollarOutlined style={{ color: '#52c41a', fontSize: '24px', marginRight: '12px' }} />
            <Title level={4} style={{ margin: 0 }}>Process Payment</Title>
          </div>
        }
        visible={visible}
        onCancel={() => {
          setVisible(false);
          form.resetFields();
          setFileList([]);
          setCurrentStep(0);
        }}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Steps current={currentStep} style={{ marginBottom: '24px' }}>
          <Step title="Order Details" />
          <Step title="Payment Info" />
          <Step title="Confirmation" />
        </Steps>

        {currentStep === 0 && (
          <div>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Order ID">#{currentOrder?.order_id}</Descriptions.Item>
              <Descriptions.Item label="Order Date">
                {moment(currentOrder?.order_date).format('DD MMM YYYY HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="Amount Due">
                <Text strong style={{ fontSize: '18px' }}>
                  {parseFloat(currentOrder?.grand_total).toLocaleString("en-US", {
                    style: "currency",
                    currency: "SAR",
                  })}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Status">
                <Badge
                  status={
                    currentOrder?.payment_status === "Paid"
                      ? "success"
                      : currentOrder?.payment_status === "Partially Paid"
                      ? "processing"
                      : "error"
                  }
                  text={currentOrder?.payment_status}
                />
              </Descriptions.Item>
            </Descriptions>
            
            <div style={{ textAlign: 'right', marginTop: '16px' }}>
              <Button 
                type="primary" 
                onClick={() => setCurrentStep(1)}
              >
                Continue to Payment
              </Button>
            </div>
          </div>
        )}

        {currentStep === 1 && (
          <Form
            form={form}
            layout="vertical"
            onFinish={() => setCurrentStep(2)}
          >
            <Form.Item
    label="Payment Method"
    validateStatus={
      isPaymentMethodTouched && !isPaymentMethodValid ? "error" : ""
    }
    help={
      isPaymentMethodTouched && !isPaymentMethodValid
        ? "Please select payment method"
        : ""
    }
  >
    <Select
      placeholder="Select payment method"
      value={paymentMethod}
      onChange={handlePaymentMethodChange}
    >
      {paymentMethods.map(method => (
        <Option key={method.value} value={method.value}>
          {method.icon} {method.label}
        </Option>
      ))}
    </Select>
  </Form.Item>

  {/* Amount Field */}
  <Form.Item
    label="Amount"
    validateStatus={isAmountTouched && !isAmountValid ? "error" : ""}
    help={
      isAmountTouched && !isAmountValid
        ? "Amount must be greater than 0"
        : ""
    }
  >
    <Input
      prefix="SAR"
      type="number"
      placeholder="Enter payment amount"
      value={amount}
      onChange={handleAmountChange}
      onBlur={() => setIsAmountTouched(true)}
    />
  </Form.Item>

  {/* Transaction ID Field */}
  <Form.Item
    label="Transaction ID/Reference"
    validateStatus={
      isTransactionIdTouched && !isTransactionIdValid ? "error" : ""
    }
    help={
      isTransactionIdTouched && !isTransactionIdValid
        ? "Please enter transaction reference"
        : ""
    }
  >
    <Input
      placeholder="Enter transaction ID or reference number"
      value={transactionId}
      onChange={handleTransactionIdChange}
      onBlur={() => setIsTransactionIdTouched(true)}
    />
  </Form.Item>


            <Form.Item label="Upload Receipt">
              <Upload
                beforeUpload={beforeUpload}
                onChange={handleUploadChange}
                fileList={fileList}
                maxCount={1}
                accept=".jpg,.jpeg,.png,.pdf"
              >
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
              <Text type="secondary">Upload payment receipt (JPG, PNG, PDF max 5MB)</Text>
            </Form.Item>

             {/* Notes Field */}
  <Form.Item label="Additional Notes">
    <TextArea
      rows={3}
      placeholder="Any additional payment details..."
      value={notes}
      onChange={handleNotesChange}
    />
  </Form.Item>

            <div style={{ textAlign: 'right' }}>
              <Button 
                style={{ marginRight: '8px' }} 
                onClick={() => setCurrentStep(0)}
              >
                Back
              </Button>
              <Button type="primary" htmlType="submit">
                Continue
              </Button>
            </div>
          </Form>
        )}

        {currentStep === 2 && (
          <div>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Order ID">#{currentOrder?.order_id}</Descriptions.Item>
              <Descriptions.Item label="Amount">
                <Text strong>
                  {form.getFieldValue('amount') ? 
                    parseFloat(form.getFieldValue('amount')).toLocaleString("en-US", {
                      style: "currency",
                      currency: "SAR",
                    }) : '0.00 SAR'
                  }
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Payment Method">
                {form.getFieldValue('payment_method')}
              </Descriptions.Item>
              <Descriptions.Item label="Transaction ID">
                {form.getFieldValue('transaction_id')}
              </Descriptions.Item>
              <Descriptions.Item label="Receipt">
                {fileList.length > 0 ? fileList[0].name : 'No receipt uploaded'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Button 
                type="primary" 
                onClick={handlePayment}
                style={{ marginRight: '8px' }}
                loading={modalLoading}
              >
                Confirm Payment
              </Button>
              <Button 
                onClick={() => setCurrentStep(1)}
                disabled={modalLoading}
              >
                Back
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RenterPayment;